import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface Notification {
  id:         string;
  type:       string;
  title:      string;
  body:       string | null;
  link:       string | null;
  read:       boolean;
  created_at: string;
}

export function useNotifications() {
  const { user }              = useAuth();
  const [items, setItems]     = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setFetchError(null);
    const { data, error } = await supabase
      .from('notifications')
      .select('id, type, title, body, link, read, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) {
      setFetchError('Erro ao carregar notificações.');
      setLoading(false);
      return;
    }
    setItems((data as Notification[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
    if (!user) return;

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setItems((prev) => [payload.new as Notification, ...prev.slice(0, 19)]);
        }
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [user, load]);

  const unreadCount = items.filter((n) => !n.read).length;

  async function markRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true } as never)
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!error) {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }
  }

  async function markAllRead(): Promise<void> {
    if (!user || unreadCount === 0) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true } as never)
      .eq('user_id', user.id)
      .eq('read', false);
    if (!error) {
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  }

  return { items, loading, fetchError, unreadCount, markRead, markAllRead };
}
